import styles from './deliveryOrderDataList.module.css'
import { Operation } from '../../../../operations'
import { Delivery, DeliveryMethod, DeliveryOrder } from '../../../state/types'
import { Async, Badge, Button, DetailFieldProps, FieldList, FieldType, FormSeparator, Link, ModalId, Section, useUIActions } from '../../../../../features/ui'
import { DeliveryOrderItemsStatsTable } from '../delivery-order-items-stats-table/deliveryOrderItemsStatsTable'
import { DeliveryDataList } from '../../delivery/delivery-data-list/deliveryDataList'
import { addressHelpers } from '../../../../../features/address'
import { Labels, useDataTypesHooks } from '../../../../../features/data-types'
import { deliveryOrderHelpers, useDeliveryOrderHooks } from '../../../hooks/deliveryOrderHooks'
import { deliveryHelpers } from '../../../hooks/deliveryHooks'
import { toDate } from '../../../../../utils/typeUtils'
import { TableName } from '../../../../../tables'
import { useNavigate } from 'react-router-dom'
import { RoutePath, useRoutesHooks } from '../../../../../features/routes'
import { toList } from '../../../../../utils/listUtils'
import { useMemo, useState } from 'react'
import { AnnulDeliveryOrderModal } from '../annul-delivery-order-modal/annulDeliveryOrderModal'

type Props = {
    deliveryOrders: DeliveryOrder[]
    deliveries: Delivery[]
    operation: Operation
    readOnly?: boolean
    onChangeCurrentDelivery: (delivery: Delivery) => void
    onRefresh?: () => void
}

export const DeliveryOrderDataList = (props: Props) => {
    const { deliveryOrders, deliveries, operation, readOnly = false, onChangeCurrentDelivery, onRefresh } = props

    const [selectedDeliveryOrder, setSelectedDeliveryOrder] = useState<DeliveryOrder>()

    const { describeDeliveryOrder, getDeliveryMethodIcon, getDeliveryMethodRoutPath, isDeliverable, isDeliveryCompletable, isAnnulable } = deliveryOrderHelpers
    const { getCreateDeliveryLabel, getCreateDeliveryIcon } = deliveryHelpers
    const { describeAddress } = addressHelpers
    const { formatDate } = useDataTypesHooks()
    const { getDeliveryAddress } = useDeliveryOrderHooks()
    const navigate = useNavigate()
    const { getRoutePath } = useRoutesHooks()
    const { openModal } = useUIActions()

    // Memoized so the nested lists keep the same deliveries reference between renders
    const deliveriesByOrder = useMemo(() => {
        const map = new Map<string, Delivery[]>()
        deliveries.forEach(delivery => {
            if (!delivery.orderId) return
            map.set(delivery.orderId, [...(map.get(delivery.orderId) || []), delivery])
        })
        return map
    }, [deliveries])

    const title = (deliveryOrder: DeliveryOrder) => `${describeDeliveryOrder(deliveryOrder)} - ${formatDate(toDate(deliveryOrder.deliveryDate))}`

    const deliveryOrderPath = (deliveryOrder: DeliveryOrder) => `${getRoutePath(getDeliveryMethodRoutPath(deliveryOrder.method))}/${deliveryOrder.id}`

    const onGoToDelivery = (deliveryOrder: DeliveryOrder) => () => {
        navigate(deliveryOrderPath(deliveryOrder))
    }

    const onCreateDelivery = (deliveryOrder: DeliveryOrder) => () => {
        const isShipment = deliveryOrder.method === DeliveryMethod.SHIPMENT
        navigate(`${deliveryOrderPath(deliveryOrder)}/${isShipment ? RoutePath.GENERATE_DELIVERY_NOTE : RoutePath.COMPLETE_DELIVERY}`)
    }

    const onCompleteDelivery = (deliveryOrder: DeliveryOrder) => () => {
        navigate(`${deliveryOrderPath(deliveryOrder)}/${RoutePath.COMPLETE_DELIVERY}`)
    }

    const onAnnulDeliveryOrder = (deliveryOrder: DeliveryOrder) => () => {
        setSelectedDeliveryOrder(deliveryOrder)
        openModal(ModalId.ANNUL_DELIVERY_ORDER)
    }

    const infoFields = (deliveryOrder: DeliveryOrder): DetailFieldProps[] => {
        const fieldList: DetailFieldProps[] = [
            {
                name: 'deliveryOrderId',
                type: FieldType.TABLE,
                table: TableName.DELIVERY_ORDERS,
                render: () => (
                    <Link
                        text={describeDeliveryOrder(deliveryOrder)}
                        icon={getDeliveryMethodIcon(deliveryOrder.method)}
                        kind="primary"
                        onClick={onGoToDelivery(deliveryOrder)}
                    />
                )
            },
            {
                name: 'deliveryDate',
                type: FieldType.TEXT,
                label: deliveryOrder.method === DeliveryMethod.PICKUP ? 'Fecha de Retiro' : 'Fecha de Entrega',
                render: () => <>{formatDate(toDate(deliveryOrder.deliveryDate))}</>
            },
            {
                name: 'deliveryAddress',
                type: FieldType.TEXT,
                label: 'Dirección de Entrega',
                render: () => <Async
                    loadFn={async () => {
                        const address = await getDeliveryAddress(deliveryOrder)!
                        return <>{describeAddress(address)}</>
                    }}
                />
            },
            {
                name: 'deliveryStatus',
                type: FieldType.TEXT,
                label: 'Estado de Entrega',
                render: () => <Badge label={Labels.deliveryStatusLabel(deliveryOrder.deliveryStatus)} />
            }
        ]
        if (deliveryOrder.items.some(item => item.quantity > 0)) {
            fieldList.push({
                name: 'deliveryOrderItems',
                type: FieldType.TABLE,
                label: '',
                render: () => (
                    <DeliveryOrderItemsStatsTable
                        deliveryOrder={deliveryOrder}
                        operation={operation}
                        title="Productos"
                    />
                )
            })
        }
        if (deliveryOrder.cancelled) {
            fieldList.push({
                name: 'cancelled',
                type: FieldType.TEXT,
                label: 'Estado',
                render: () => <Badge label='Cancelada' />
            })
        }
        const deliveriesForOrder = deliveriesByOrder.get(deliveryOrder.id) || []
        if (deliveriesForOrder.length > 0) {
            fieldList.push({
                name: 'deliveries-separator',
                type: FieldType.TEXT,
                render: () => <FormSeparator title="Remitos" />
            })
            fieldList.push({
                name: 'deliveries',
                type: FieldType.TABLE,
                label: '',
                render: () => (
                    <DeliveryDataList
                        deliveries={deliveriesForOrder}
                        deliveryOrders={toList(deliveryOrder)}
                        operations={toList(operation)}
                        defaultExpanded={false}
                        onChangeCurrentDelivery={onChangeCurrentDelivery}
                        onRefresh={onRefresh}
                    />
                )
            })
        }
        return fieldList
    }

    const renderActions = (deliveryOrder: DeliveryOrder) => {
        if (readOnly) return <></>

        const isShipment = deliveryOrder.method === DeliveryMethod.SHIPMENT
        const hasDeliveries = (deliveriesByOrder.get(deliveryOrder.id) || []).length > 0
        const showCreateDelivery = isDeliverable(deliveryOrder)
        const showCompleteDelivery = isShipment && isDeliveryCompletable(deliveryOrder)
        const showAnnul = isAnnulable(deliveryOrder)
        if (!showCreateDelivery && !showCompleteDelivery && !showAnnul) return <></>

        return (
            <div className={styles.actions}>
                {showCreateDelivery && (
                    <Button
                        icon={getCreateDeliveryIcon(deliveryOrder.method)}
                        tooltipLines={getCreateDeliveryLabel(deliveryOrder.method).split(' ')}
                        small
                        onClick={onCreateDelivery(deliveryOrder)}
                    />
                )}
                {showCompleteDelivery && (
                    <Button
                        icon="bag-check-fill"
                        tooltipLines={['Completar', 'Envío']}
                        small
                        onClick={onCompleteDelivery(deliveryOrder)}
                    />
                )}
                {showAnnul && (
                    <Button
                        icon="slash-circle"
                        tooltipLines={['Anular', hasDeliveries ? 'Pendientes' : 'Orden']}
                        kind="secondary"
                        small
                        onClick={onAnnulDeliveryOrder(deliveryOrder)}
                    />
                )}
            </div>
        )
    }

    return (
        <div className={styles.container}>
            {[...deliveryOrders]
                .sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime() || b.code - a.code)
                .map(deliveryOrder => {
                    return (
                        <Section
                            key={deliveryOrder.id}
                            id={`delivery-order-${deliveryOrder.id}`}
                            title={title(deliveryOrder)}
                            collapsable
                            space={12}
                        >
                            <FieldList fields={infoFields(deliveryOrder)} />
                            {renderActions(deliveryOrder)}
                        </Section>
                    )
                })}
            <AnnulDeliveryOrderModal
                deliveryOrder={selectedDeliveryOrder}
                operation={operation}
                onAnnul={onRefresh}
            />
        </div>
    )
}
